/**
 * CreateLifecycleActions Component
 *
 * Container component that renders lifecycle action buttons for CREATE mode.
 * Filters actions to only those valid from 'draft' state (the initial state
 * a new record will be in).
 *
 * When approval workflow is disabled: Shows "Create & Activate"
 * When approval workflow is enabled: Shows "Create & Submit"
 */

import { Space } from 'antd'
import { CreateLifecycleActionButton } from './CreateLifecycleActionButton'
import type { LifecycleMetadata } from '@/services/metadataService'

/**
 * Props for CreateLifecycleActions container
 */
export interface CreateLifecycleActionsProps {
  /** Lifecycle metadata from backend */
  lifecycle: LifecycleMetadata
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Key field name (default: 'uid') */
  keyField?: string
  /** Function to validate and get form values */
  getFormValues: () => Promise<Record<string, unknown> | null>
  /** Called after successful create + action */
  onSuccess?: (record: Record<string, unknown>) => void
  /** Whether the form is currently submitting */
  disabled?: boolean
}

/**
 * CreateLifecycleActions Component
 *
 * Renders lifecycle action buttons available from 'draft' state for use
 * in CREATE mode. Provides "Create & [Action]" functionality.
 *
 * @example
 * ```tsx
 * <CreateLifecycleActions
 *   lifecycle={metadata.lifecycle}
 *   endpoint="/offerings"
 *   entityLabel="Offering"
 *   getFormValues={async () => {
 *     try {
 *       return await form.validateFields()
 *     } catch {
 *       return null
 *     }
 *   }}
 *   onSuccess={(record) => {
 *     invalidateQueries()
 *     closeDrawer()
 *   }}
 * />
 * ```
 */
export function CreateLifecycleActions({
  lifecycle,
  endpoint,
  entityLabel,
  keyField = 'uid',
  getFormValues,
  onSuccess,
  disabled = false,
}: CreateLifecycleActionsProps) {
  // Filter actions to only those valid from 'draft' state
  // These are the actions a newly created record can take
  const applicableActions = Object.entries(lifecycle.actions).filter(
    ([, action]) => action.fromStates.includes('draft')
  )

  // Exclude 'duplicate' action - doesn't make sense for create mode
  const filteredActions = applicableActions.filter(
    ([actionKey]) => actionKey !== 'duplicate'
  )

  // No applicable actions
  if (filteredActions.length === 0) {
    return null
  }

  // Sort by order if available
  const sortedActions = [...filteredActions].sort((a, b) => {
    const orderA = a[1].order ?? 99
    const orderB = b[1].order ?? 99
    return orderA - orderB
  })

  return (
    <Space size={8} wrap={false}>
      {sortedActions.map(([actionKey, action]) => (
        <CreateLifecycleActionButton
          key={actionKey}
          actionKey={actionKey}
          action={action}
          endpoint={endpoint}
          entityLabel={entityLabel}
          keyField={keyField}
          getFormValues={getFormValues}
          onSuccess={onSuccess}
          disabled={disabled}
        />
      ))}
    </Space>
  )
}
